import { Grid, csx, cx, useBreakpoint } from '@vtex/admin-ui'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  ProductCarouselButton,
  useDefaultMobileCardSize,
} from '@/checkout-instore-legacy/components/ProductCarousel'

const PRODUCTS_PER_PAGE_DEFAULT = 4

export type ProductCarouselPaginatedListHandle = {
  currentPage: number
  goToNextPage: () => void
  goToPreviousPage: () => void
} & Partial<HTMLDivElement>

export type ProductCarouselPaginatedListProps = {
  isLoading?: boolean
  itemsPerPage?: number
  totalCount: number
  onClickPrev: () => void
  onClickNext: () => void
  onSwipeToRight?: () => void
} & React.ComponentPropsWithoutRef<'div'>

// TODO: remove duplicated logic between this component and checkout-instore/ProductCarouselList
export const ProductCarouselPaginatedList = forwardRef<
  ProductCarouselPaginatedListHandle,
  ProductCarouselPaginatedListProps
>(function ProductCarouselPaginatedList(
  {
    children,
    isLoading,
    totalCount,
    itemsPerPage = PRODUCTS_PER_PAGE_DEFAULT,
    // TODO: this error popped up when moving the checkout-instore project
    // to checkout-instore-legacy, but needs to be fixed at some point
    // eslint-disable-next-line react/prop-types
    className = '',
    onClickPrev,
    onClickNext,
    onSwipeToRight,
    ...props
  },
  ref
) {
  const listRef = useRef<HTMLDivElement>(null)
  const observerTarget = useRef<HTMLDivElement>(null)

  const { breakpoint } = useBreakpoint()
  const isMobileOrTablet = ['mobile', 'tablet'].includes(breakpoint)

  const [currentProductsListPage, setCurrentProductsListPage] = useState(0)
  const childrenCount = React.Children.count(children) - 1

  const [translateXPercentage, setTranslateXPercentage] = useState(0)
  const [offset, setOffset] = useState(0)

  const defaultMobileCardSize = useDefaultMobileCardSize()

  useImperativeHandle(
    ref,
    () => {
      return {
        currentPage: currentProductsListPage,
        goToNextPage() {
          setCurrentProductsListPage((prev) => prev + 1)
        },
        goToPreviousPage() {
          setCurrentProductsListPage((prev) => prev - 1)
        },
      }
    },
    [currentProductsListPage]
  )

  useEffect(() => {
    const observerTargetCurrent = observerTarget.current

    if (!isMobileOrTablet) return
    // A way to create infinite horizontal pagination on mobile devices
    // We define a observer that check if the scroll of list
    // has intersected by a contianer element added always in the final of the list
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onSwipeToRight?.()
        }
      },
      { threshold: 1 }
    )

    if (observerTargetCurrent) {
      observer.observe(observerTargetCurrent)
    }

    return () => {
      if (observerTargetCurrent) {
        observer.unobserve(observerTargetCurrent)
      }
    }
  }, [observerTarget, isMobileOrTablet, onSwipeToRight])

  useEffect(() => {
    setTranslateXPercentage(-100 * currentProductsListPage)

    // Correct for last page. Example:
    // 4 products, 3 per page
    // [ 1 2 3 ] 4 X Y
    // click next page would result in:
    // 1 2 3 [ 4 X Y ]
    // Here diff = 2/3 since lastSlotPosition (Y) = 6, childrenCount = 4
    // and itemsPerPage = 3. So correcting by 2/3 yields:
    // 1 [ 2 3 4 ] X Y
    // Keep offset until we're back to the first page.
    const lastSlotPosition = itemsPerPage * (1 + currentProductsListPage)

    if (currentProductsListPage > 0 && lastSlotPosition > childrenCount) {
      const emptyPercentage = (lastSlotPosition - childrenCount) / itemsPerPage

      setOffset(100 * emptyPercentage)
    } else if (currentProductsListPage === 0) {
      setOffset(0)
    }
  }, [currentProductsListPage, childrenCount, itemsPerPage])

  return (
    <div
      className={cx(
        csx({
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }),
        className
      )}
    >
      <div
        {...props}
        ref={listRef}
        className={csx({
          display: 'flex',
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          '::-webkit-scrollbar': {
            display: 'none',
          },
          '@desktop': {
            display: 'block',
            overflow: 'hidden',
          },
        })}
      >
        <Grid
          className={csx({
            transform: `translateX(${translateXPercentage + offset}%)`,
            display: 'inline-grid',
            gridAutoFlow: 'column',
            gridAutoColumns: defaultMobileCardSize,
            margin: '-0.5rem',
            '@desktop': {
              display: 'grid',
              transition: 'transform 0.4s ease',
              gridAutoColumns: `${100 / itemsPerPage}%`,
              margin: '-1.25rem',
            },
          })}
        >
          {children}
        </Grid>
        {isMobileOrTablet && <div ref={observerTarget} />}
      </div>
      {!isLoading && currentProductsListPage > 0 && (
        <ProductCarouselButton
          data-testid="product-carousel-button-prev"
          direction="previous"
          onClick={onClickPrev}
        />
      )}
      {!isLoading &&
        (1 + currentProductsListPage) * itemsPerPage < totalCount && (
          <ProductCarouselButton
            data-testid="product-carousel-button-next"
            direction="next"
            onClick={onClickNext}
          />
        )}
    </div>
  )
})
